import { template as template_0a053f500b3e413faebe5c040ef0afb1 } from "@ember/template-compiler";
import { action } from '@ember/object';
import { service } from '@ember/service';
import { all, task, taskGroup, timeout } from 'ember-concurrency';
import { isEmpty, isPresent } from '@ember/utils';
import getDelayTime from 'eflex/util/get-delay-time';
import wieSettings from 'eflex/constants/work-instructions/wie-settings';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { waitFor } from '@ember/test-waiters';
import { TrackedArray } from 'tracked-built-ins';
import { addObjectsIfNotPresent, removeObjects, removeObject, addObjectIfNotPresent } from 'eflex/util/array-helpers';
import { sortByProp } from 'ramda-adjunct';
import { range, groupBy } from 'ramda';
import { IMPORT_TYPES_ALLOWED_AS_WI, ALLOWED_EXTENSIONS, MAX_UPLOAD_SIZE, MAX_UPLOAD_SIZE_MB, MIME_TYPE_DESCRIPTIONS } from 'eflex/constants/file-types';
import Portal from 'eflex/components/portal';
import PortalTarget from 'ember-stargate/components/portal-target';
import BsButton from 'eflex/components/bs-button';
import BsButtonGroup from 'eflex/components/bs-button-group';
import SearchLive from 'eflex/components/inputs/search-live';
import FileInput from 'eflex/components/file-input';
import ListView from 'eflex/components/work-instruction-editor/library-views/list-view';
import ApprovalView from 'eflex/components/work-instruction-editor/library-views/approval-view';
import ThumbView from 'eflex/components/work-instruction-editor/library-views/thumb-view';
import UrlViewer from 'eflex/components/url-viewer';
import NumberControl from 'eflex/components/bs-form/element/control/number';
import ConfirmModal from 'eflex/components/work-instruction-editor/confirm-modal';
import StandardModal from 'eflex/components/standard-modal';
import NoRouteTabs from 'eflex/components/no-route-tabs';
import Spinner from 'eflex/components/spinner';
import ActionDropdown from 'eflex/components/work-instruction-editor/library-card/action-dropdown';
import BsForm from 'eflex/components/bs-form';
import onKey from 'ember-keyboard/modifiers/on-key';
import TagsSelect from 'eflex/components/tags-select';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didUpdate from '@ember/render-modifiers/modifiers/did-update';
import BsTooltip from 'eflex/components/bs-tooltip';
import FaIcon from '@fortawesome/ember-fontawesome/components/fa-icon';
import AddButton from 'eflex/components/add-button';
import { t } from 'ember-intl';
import { or, not, and, eq } from 'ember-truth-helpers';
import { get, fn } from '@ember/helper';
import { autofocus } from 'eflex/modifiers';
const LIBRARY_VIEWS_NEEDING_RELOAD = new Set([
    'thumb',
    'list'
]);
export default class WorkInstructionEditorLibrary extends Component {
    @service
    imageEditor;
    @service
    currentUser;
    @service
    workInstructionRepo;
    @service
    hostedFileRepo;
    @service
    intl;
    @service
    store;
    @service
    fileUploader;
    @service
    notifier;
    @tracked
    selectedCards = new TrackedArray();
    @tracked
    selectedFolder = this.args.currentWorkInstruction?.folder || this.args.selectedFolder;
    @tracked
    isAllSelected = false;
    @tracked
    lastSelected;
    @tracked
    targetCard;
    @tracked
    newReplacementFile;
    @tracked
    showFolderSettingsModal = false;
    @tracked
    currentLibraryView = wieSettings.libraryViews.THUMB;
    @tracked
    pendingChangeReviewCount;
    @tracked
    searchTerm;
    @tracked
    recordCount = 0;
    @tracked
    pageNumber = 1;
    @tracked
    filteredFiles = new TrackedArray();
    @tracked
    filteredLibraryItems = new TrackedArray();
    @tracked
    filteredFolders = new TrackedArray();
    @tracked
    showConfirmModal = false;
    @tracked
    confirmModalText;
    @tracked
    confirmAction;
    @tracked
    showImportModal = false;
    @tracked
    confirmImportText;
    @tracked
    importFiles;
    @tracked
    showExpandPreviewModal = false;
    supportedFilesAsInstruction = IMPORT_TYPES_ALLOWED_AS_WI;
    supportedFileTypes = ALLOWED_EXTENSIONS;
    mimeTypeDescriptions = MIME_TYPE_DESCRIPTIONS;
    previousLibraryView;
    @taskGroup({
        enqueue: true
    })
    loadTasks;
    get currentTab() {
        return this.isApprovalView ? 'approval' : 'library';
    }
    get userHasRole() {
        const { user } = this.currentUser;
        return user.isAdmin || isPresent(user.wieDeployerTags) || isPresent(user.wieEditorTags) || isPresent(user.wieApproverTags);
    }
    get isThumbView() {
        return this.currentLibraryView === wieSettings.libraryViews.THUMB;
    }
    get isListView() {
        return this.currentLibraryView === wieSettings.libraryViews.LIST;
    }
    get isApprovalView() {
        return this.currentLibraryView === wieSettings.libraryViews.APPROVAL;
    }
    get showSpinner() {
        return this.search.isRunning || this.loadFolder.isRunning || this.loadTasks.isRunning || this.importWorkInstructions.isRunning || this.confirmImport.isRunning || this.moveToFolder.isRunning || this.deleteConfirm.isRunning || this._loadMore.isRunning || this.saveFolder.isRunning || this.import.isRunning || this._uploadFile.isRunning || this.replace.isRunning;
    }
    get workInstructionCount() {
        return this.selectableLibraryItems.length;
    }
    get showLoadButton() {
        return (this.workInstructionCount > 0 && (!this.isApprovalView ? this.workInstructionCount < this.recordCount : this.workInstructionCount < this.pendingChangeReviewCount));
    }
    get selectedWorkInstructions() {
        return this.workInstructionRepo.workInstructions.filter((item)=>item.isSelected);
    }
    get selectableFolders() {
        if (this.selectedFolder != null) {
            return [];
        }
        const folders = sortByProp('name', this.filteredFolders.filter((folder)=>!folder.isNew));
        const newFolder = this.filteredFolders.find((folder)=>folder.isNew);
        if (newFolder != null) {
            folders.unshift(newFolder);
        }
        return folders;
    }
    get selectableLibraryItems() {
        const instructions = this.filteredLibraryItems.filter((instruction)=>!instruction.isNew && (!this.args.isModal || instruction.deployed));
        const hostedFiles = this.filteredFiles;
        let libraryItems = instructions.concat(hostedFiles);
        if (this.isApprovalView) {
            libraryItems = instructions.filter((wi)=>wi.approvalRequested);
        } else if (this.selectedFolder) {
            libraryItems = libraryItems.filter((item)=>item.folder === this.selectedFolder);
        } else if (isEmpty(this.searchTerm)) {
            libraryItems = libraryItems.filter((item)=>!item.folder);
        }
        return sortByProp('name', libraryItems);
    }
    get isMultiselectMode() {
        return this.selectedCards.length > 1;
    }
    tabBecameActive = (tab)=>{
        if (tab === 'approval') {
            this.currentLibraryView = wieSettings.libraryViews.APPROVAL;
        } else {
            this.currentLibraryView = wieSettings.libraryViews.THUMB;
        }
        this.resetList.perform();
    };
    search = task({
        restartable: true
    }, waitFor(async ()=>{
        this.pageNumber = 1;
        if (isEmpty(this.searchTerm)) {
            return;
        }
        const { folders, instructions, files, count } = await this._queryLibraryItems.perform();
        Object.assign(this, {
            recordCount: count,
            filteredFiles: new TrackedArray(files),
            filteredFolders: new TrackedArray(folders),
            filteredLibraryItems: new TrackedArray(instructions),
            selectedCards: new TrackedArray()
        });
    }));
    get isEditorOnly() {
        return this.workInstructionRepo.userIsEditorOnly();
    }
    loadFolder = task(waitFor(async (folder)=>{
        if (folder == null && this.selectedFolder.isInvalid) {
            // Reset the folder to have its previous (i.e. non-blank) name.
            this.selectedFolder.rollbackAttributes();
        }
        Object.assign(this, {
            searchTerm: null,
            pageNumber: 1,
            selectedCards: new TrackedArray(),
            selectedFolder: folder
        });
        this.args.setSelectedFolder?.(folder);
        if (folder == null) {
            await this.resetList.perform();
            return;
        }
        const { instructions, files, count } = await this._queryLibraryItems.perform();
        Object.assign(this, {
            recordCount: count,
            filteredLibraryItems: new TrackedArray(instructions),
            filteredFiles: new TrackedArray(files),
            filteredFolders: new TrackedArray()
        });
        if (this.args.currentWorkInstruction?.isNew) {
            this._addRecordToFilteredInstructions(this.args.currentWorkInstruction);
        }
    }));
    // jscpd:ignore-start
    _loadMore = task(waitFor(async ()=>{
        let { folders, instructions, files, count } = await this._queryLibraryItems.perform();
        if (this.selectedFolder != null) {
            folders = [];
        }
        addObjectsIfNotPresent(this.filteredLibraryItems, instructions);
        addObjectsIfNotPresent(this.filteredFiles, files);
        addObjectsIfNotPresent(this.filteredFolders, folders);
        this.recordCount = count;
    }));
    setPendingChangeReviewCount = task(waitFor(async ()=>{
        const query = await this.workInstructionRepo.queryInstructions.perform({
            approvable: true
        });
        this.pendingChangeReviewCount = query.instructions.length;
    }));
    resetList = task({
        group: 'loadTasks'
    }, waitFor(async ()=>{
        Object.assign(this, {
            searchTerm: null,
            pageNumber: 1,
            selectedCards: new TrackedArray()
        });
        const { folders, instructions, files, count } = await this._queryLibraryItems.perform();
        Object.assign(this, {
            recordCount: count,
            filteredFiles: new TrackedArray(files),
            filteredFolders: new TrackedArray(folders),
            filteredLibraryItems: new TrackedArray(instructions)
        });
        if (!this._isCurrentInstructionDisplayed(this.args.currentWorkInstruction)) {
            this._addRecordToFilteredInstructions(this.args.currentWorkInstruction);
        }
        if (this.workInstructionRepo.isApprovalActive && !this.args.isModal) {
            this.setPendingChangeReviewCount.perform();
        }
        this.clearSelections();
    }));
    // jscpd:ignore-end
    importWorkInstructions = task(waitFor(async (files)=>{
        this.importFiles = [
            ...files
        ];
        const canImportAsInstruction = this.importFiles.filter((file)=>this.supportedFilesAsInstruction.includes(file.type));
        if (canImportAsInstruction.length > 0) {
            this.showImportModal = true;
            this.confirmImportText = this.intl.t('workInstructions.importText', {
                count: canImportAsInstruction.length
            });
        } else {
            await this.confirmImport.perform();
        }
    }));
    confirmImport = task(waitFor(async (asWorkInstruction = false)=>{
        let toImport = this.importFiles;
        if (asWorkInstruction) {
            const { importAsInstruction, importAsFile } = groupBy((file)=>(this.supportedFilesAsInstruction.includes(file.type) ? 'importAsInstruction' : 'importAsFile'), toImport);
            await all(importAsInstruction.map((file)=>this._importWorkInstruction.perform(file)));
            toImport = importAsFile ?? [];
        }
        if (toImport.length > 0) {
            await all(toImport.map((file)=>this._uploadFile.perform(file)));
        }
        this.importFiles = null;
        this.showImportModal = false;
        await this.resetList.perform();
    }));
    cancelImport = ()=>{
        this.showImportModal = false;
        this.importFiles = null;
    };
    _importWorkInstruction = task({
        enqueue: true,
        maxConcurrency: 3
    }, waitFor(async (file)=>{
        const image = new Image();
        const { blobToDataURL } = await import('blob-util');
        image.src = await blobToDataURL(file);
        await image.decode();
        const workInstruction = this.workInstructionRepo.createWorkInstruction({
            name: file.name,
            folder: this.selectedFolder,
            width: image.naturalWidth,
            height: image.naturalHeight,
            canvas: await this.imageEditor.getImageCanvasJson.perform(image),
            displayImageData: image.src
        });
        await this.workInstructionRepo.saveWithImageData.perform(workInstruction);
    }));
    delete = task({
        drop: true
    }, waitFor(async (libraryItem)=>{
        let selectedFolderCount, selectedFilesCount, selectedInstructionCount;
        const text = [];
        if (!libraryItem) {
            selectedFolderCount = this.selectedCards.filter((card)=>card.isFolder).length;
            selectedFilesCount = this.selectedFiles.length;
            selectedInstructionCount = this.selectedCards.length - selectedFolderCount - this.selectedFiles.length;
        } else {
            this.targetCard = libraryItem;
            selectedFolderCount = libraryItem.isFolder ? 1 : 0;
            selectedFilesCount = libraryItem.isFile ? 1 : 0;
            selectedInstructionCount = libraryItem.isInstruction ? 1 : 0;
        }
        if (selectedFolderCount > 0) {
            text.push(this.intl.t('workInstructions.delete.folderWarning', {
                count: selectedFolderCount
            }));
        }
        if (selectedInstructionCount > 0) {
            text.push(this.intl.t('workInstructions.delete.warning', {
                count: selectedInstructionCount
            }));
        }
        if (selectedFilesCount > 0) {
            const instructionsUsingFiles = await this.store.query('work-instruction', {
                backgroundIds: libraryItem ? libraryItem.id : this.selectedFiles.map((item)=>item.id)
            });
            let deletedFileText = this.intl.t('workInstructions.deleteFiles', {
                count: selectedFilesCount
            });
            if (instructionsUsingFiles.length > 0) {
                deletedFileText += this.intl.t('workInstructions.instructionsAffected', {
                    count: instructionsUsingFiles.length
                });
            }
            text.push(deletedFileText);
        }
        this.confirmModalText = text;
        this.showConfirmModal = true;
        this.confirmAction = 'delete';
    }));
    deleteConfirm = task({
        drop: true
    }, waitFor(async ()=>{
        let toDelete;
        if (!this.targetCard && isPresent(this.selectedCards)) {
            toDelete = this.selectedCards;
        } else {
            toDelete = [
                this.targetCard
            ];
        }
        await this.args?.onDelete(await this.workInstructionRepo.deleteSelectedLibraryItems.perform(this.args.currentWorkInstruction, toDelete));
        removeObjects(this.filteredFolders, toDelete);
        removeObjects(this.filteredLibraryItems, toDelete);
        if (toDelete.includes(this.args.currentWorkInstruction?.folder)) {
            removeObject(this.filteredLibraryItems, this.args.currentWorkInstruction);
        }
        this.selectedCards = new TrackedArray();
        this.targetCard = null;
        await this.resetList.perform();
        this.showConfirmModal = false;
    }));
    saveFolder = task(waitFor(async (folder)=>{
        if (!folder.isDirty || folder.isInvalid) {
            return;
        }
        await folder.save();
    }));
    dropOnFolder = task(waitFor(async (folder, libraryItem)=>{
        const selectedLibraryItems = addObjectIfNotPresent(this.selectedCards.filter((card)=>!card.isFolder), libraryItem);
        const selectedWorkInstructions = [];
        selectedLibraryItems.forEach((item)=>{
            item.folder = folder;
            selectedWorkInstructions.push(item);
        });
        await this.workInstructionRepo.saveInstructions.perform(selectedWorkInstructions);
        await this._loadMore.perform();
        this.selectedCards = new TrackedArray();
    }));
    delistInstructions = task(waitFor(async (instructions)=>{
        removeObjects(this.filteredLibraryItems, instructions);
        this.recordCount = this.recordCount - instructions.length;
        if (this.filteredLibraryItems.length > this.workInstructionRepo.perPage && this.filteredLibraryItems.length > this.recordCount) {
            await this._loadMore.perform();
        }
    }));
    moveToFolder = task({
        drop: true
    }, waitFor(async (folder, libraryItem)=>{
        let toMove;
        if (!libraryItem && isPresent(this.selectedCards)) {
            toMove = this.selectedCards;
        } else {
            toMove = [
                libraryItem
            ];
        }
        await all(toMove.map((item)=>{
            item.folder = folder;
            removeObject(this.filteredLibraryItems, item);
            return item.save();
        }));
    }));
    setView = task(waitFor(async (libraryView)=>{
        this.previousLibraryView = this.currentLibraryView;
        this.currentLibraryView = libraryView;
        this.clearSelections();
        if ((LIBRARY_VIEWS_NEEDING_RELOAD.has(this.previousLibraryView) && libraryView === 'approval') || (this.previousLibraryView === 'approval' && LIBRARY_VIEWS_NEEDING_RELOAD.has(libraryView))) {
            await this._resetLibrary.perform();
        }
    }));
    toggleView = ()=>{
        if (this.currentLibraryView === 'thumb') {
            this.setView.perform('list');
        } else {
            this.setView.perform('thumb');
        }
    };
    selectedConfirmAction = task(waitFor(async ()=>{
        switch(this.confirmAction){
            case 'replace':
                {
                    await this.replaceConfirm.perform(this.newReplacementFile, this.targetCard);
                    break;
                }
            case 'delete':
                {
                    await this.deleteConfirm.perform();
                    break;
                }
        }
        this.showConfirmModal = false;
    }));
    addFolder = ()=>{
        const newFolder = this.workInstructionRepo.createFolder();
        this.folderSettings(newFolder);
    };
    _queryInstructions = task(waitFor(async ()=>{
        const query = {
            searchTerm: this.searchTerm,
            pageNumber: this.pageNumber,
            folder: this.selectedFolder?.id
        };
        if (this.isApprovalView) {
            query.approvable = true;
            delete query.folder;
        }
        return await this.workInstructionRepo.queryInstructions.perform(query);
    }));
    _queryHostedFiles = task(waitFor(async ()=>{
        const query = {
            searchTerm: this.searchTerm,
            pageNumber: this.pageNumber,
            folder: this.selectedFolder?.id
        };
        return await this.hostedFileRepo.queryHostedFiles.perform(query);
    }));
    _queryLibraryItems = task(waitFor(async ()=>{
        const folders = this.selectedFolder ? [] : this.workInstructionRepo.searchFolders(this.searchTerm);
        const { instructions, count } = await this._queryInstructions.perform();
        const { files, count: fileCount } = await this._queryHostedFiles.perform();
        return {
            folders,
            instructions,
            files,
            count: count + fileCount
        };
    }));
    _resetLibrary = task(waitFor(async ()=>{
        Object.assign(this, {
            selectedFolder: null,
            selectedCards: new TrackedArray()
        });
        this.args.setSelectedFolder?.(null);
        await this.resetList.perform();
    }));
    _searchDebounced = task({
        restartable: true
    }, waitFor(async ()=>{
        await timeout(getDelayTime(300));
        await this.search.perform();
    }));
    onSearchInput = task(waitFor(async (searchTerm)=>{
        this.searchTerm = searchTerm;
        if (!isEmpty(this.searchTerm)) {
            await this._searchDebounced.perform();
        } else {
            await this.resetList.perform();
        }
    }));
    loadMore = task(waitFor(async ()=>{
        this.pageNumber += 1;
        await this._loadMore.perform();
    }));
    constructor(){
        super(...arguments);
        this.resetList.perform();
    }
    onDidUpdate = task({
        group: 'loadTasks'
    }, waitFor(async (element, [currentWorkInstruction])=>{
        await timeout(0);
        if (!this._isCurrentInstructionDisplayed(currentWorkInstruction)) {
            this._addRecordToFilteredInstructions(currentWorkInstruction);
        }
    }));
    folderSettings = (libraryCard)=>{
        this.targetCard = libraryCard;
        this.showFolderSettingsModal = true;
    };
    edit = task(waitFor(async (libraryCard)=>{
        if (libraryCard.isFile) {
            this.setCardSelectedTrue(libraryCard);
            this.showExpandPreviewModal = true;
            return;
        }
        this.selectedCards = new TrackedArray();
        this.setView.perform(wieSettings.libraryViews.THUMB);
        await this.args.loadWorkInstruction?.(libraryCard);
    }));
    loadWorkInstruction = task(waitFor(async (workInstruction)=>{
        if (this.args.setWorkInstruction) {
            await this.args.stashWorkInstruction(workInstruction);
            await this.args.setWorkInstruction();
        } else {
            if (workInstruction === this.args.currentWorkInstruction) {
                return;
            }
            await this.edit.perform(workInstruction);
        }
    }));
    confirmFolderSettings = task(waitFor(async ()=>{
        if (this.targetCard.isNew) {
            this.filteredFolders.push(this.targetCard);
            this.recordCount += 1;
        }
        if (!this.targetCard.name) {
            this.targetCard.name = this.intl.t('workInstructions.untitledFolder');
        }
        await this.saveFolder.perform(this.targetCard);
        this.showFolderSettingsModal = false;
        this.targetCard = null;
    }));
    @action
    clearSelections() {
        this.selectableLibraryItems.forEach((libraryItem)=>{
            libraryItem.isSelected = false;
        });
        this.selectableFolders.forEach((folder)=>{
            folder.isSelected = false;
        });
        Object.assign(this, {
            selectedCards: new TrackedArray(),
            isAllSelected: false
        });
    }
    @action
    selectAll(value) {
        this.isAllSelected = value;
        if (!this.isAllSelected) {
            this.clearSelections();
            return;
        }
        this.selectableLibraryItems.concat(this.selectableFolders).filter((libraryItem)=>!this.selectedCards.includes(libraryItem)).forEach((libraryItem)=>{
            libraryItem.isSelected = true;
            this.selectedCards.push(libraryItem);
        });
    }
    @action
    cancelFolderSettings() {
        this.targetCard.rollbackAttributes();
        this.showFolderSettingsModal = false;
        this.targetCard = null;
    }
    cardSelected = (libraryItem, e)=>{
        const currentlySelected = this.lastSelected === libraryItem;
        if (e.shiftKey && !this.args.multiselectDisabled) {
            this.cardMultiselect(libraryItem);
        } else {
            const multipleSelected = this.selectedCards.length > 1;
            if (currentlySelected && !multipleSelected) {
                this.lastSelected = null;
                this.clearSelections();
                libraryItem.isSelected = false;
                removeObject(this.selectedCards, libraryItem);
            } else {
                this.lastSelected = libraryItem;
                this.clearSelections();
                libraryItem.isSelected = true;
                addObjectIfNotPresent(this.selectedCards, this.lastSelected);
                if (!this.lastSelected.isFolder) {
                    this.args.stashWorkInstruction?.(this.lastSelected);
                }
            }
        }
    };
    cardMultiselect = (libraryItem)=>{
        const currentlySelected = this.lastSelected === libraryItem;
        if (!currentlySelected) {
            this.lastSelected = libraryItem;
        }
        if (this.selectedCards.includes(this.lastSelected)) {
            libraryItem.isSelected = false;
            removeObject(this.selectedCards, this.lastSelected);
        } else {
            libraryItem.isSelected = true;
            addObjectIfNotPresent(this.selectedCards, this.lastSelected);
        }
    };
    setCardSelectedTrue = (libraryItem)=>{
        if (!libraryItem.isSelected) {
            this.lastSelected = libraryItem;
            this.clearSelections();
            libraryItem.isSelected = true;
            addObjectIfNotPresent(this.selectedCards, this.lastSelected);
        }
    };
    deselectAll = (e = null)=>{
        if (!e || (!e.target.closest('.library-card') && this.selectedCards.length > 0)) {
            this.clearSelections();
        }
    };
    _addRecordToFilteredInstructions(instructionToAdd) {
        if (!instructionToAdd) {
            return;
        }
        addObjectIfNotPresent(this.filteredLibraryItems, instructionToAdd);
        if (instructionToAdd.isNew && this.showLoadButton) {
            this.recordCount += 1;
        }
    }
    _isCurrentInstructionDisplayed(currentWorkInstruction) {
        if (currentWorkInstruction == null || currentWorkInstruction.isDestroyed) {
            return false;
        }
        return this.filteredLibraryItems.includes(currentWorkInstruction);
    }
    get selectedFiles() {
        return this.hostedFileRepo.hostedFiles.filter((item)=>item.isSelected);
    }
    import = task(waitFor(async (fileList)=>{
        await all(range(0, fileList.length).map((i)=>this._uploadFile.perform(fileList.item(i))));
    }));
    _uploadFile = task(waitFor(async (file, replacedFile = null)=>{
        if (file.size > MAX_UPLOAD_SIZE) {
            this.notifier.sendError('fileTooLarge', {
                filename: file.name,
                size: MAX_UPLOAD_SIZE_MB
            });
            return;
        }
        const folder = this.selectedFolder?.id || '';
        let response;
        if (isEmpty(replacedFile)) {
            response = await this.fileUploader.post.perform('hostedFiles', file, {
                folder
            });
        } else {
            response = await this.fileUploader.put.perform(`hostedFiles/${replacedFile.id}`, file, {
                folder
            });
        }
        this.store.push(this.store.normalize('hosted-file', response.hostedFile));
    }));
    replace = task(waitFor(async (newFiles, resetInput, fileBeingReplaced, callback)=>{
        const instructions = await this.store.query('work-instruction', {
            backgroundIds: fileBeingReplaced.id
        });
        this.newReplacementFile = newFiles.item(0);
        if (instructions.length > 0) {
            Object.assign(this, {
                confirmModalText: [
                    this.intl.t('workInstructions.instructionsAffected', {
                        count: instructions.length
                    })
                ],
                showConfirmModal: true,
                confirmAction: 'replace',
                targetCard: fileBeingReplaced
            });
        } else {
            await this.replaceConfirm.perform(this.newReplacementFile, fileBeingReplaced, callback);
        }
        resetInput?.();
    }));
    replaceConfirm = task({
        enqueue: true
    }, waitFor(async (newFile, fileBeingReplaced, callback)=>{
        await this._uploadFile.perform(newFile, fileBeingReplaced);
        Object.assign(this, {
            newReplacementFile: null,
            targetCard: null
        });
        await this.resetList.perform();
        if (this.args.currentWorkInstruction?.canvas) {
            const backgroundImageUrl = this.args.currentWorkInstruction.canvas.backgroundImage.src;
            await this.imageEditor.renderReplaceBackground.perform(backgroundImageUrl);
        }
        callback?.();
    }));
    static{
        template_0a053f500b3e413faebe5c040ef0afb1(`
    {{#if (and this.workInstructionRepo.isApprovalActive this.userHasRole (not @isModal))}}
      <Portal @target="tab-bar">
        <NoRouteTabs
          @tabBecameActive={{this.tabBecameActive}}
          @activeTab={{this.currentTab}}
          as |tabs|
        >
          <tabs.navigation class="pt-2 mb-0" as |nav|>
            <nav.tab @href='library' class='tab-library'>
              {{t "library"}}
            </nav.tab>
            <nav.tab @href='approval' class='tab-approvals' @onClick={{fn (mut this.currentLibraryView) 'approval'}}>
              <div class="d-flex align-items-center">
                <FaIcon @icon="clipboard-check" @prefix="fas" class="me-2" />
                <span>{{t "workInstructions.changeReviews"}}</span>
                <span
                  class="badge badge-pill fs-5 {{if this.pendingChangeReviewCount "bg-danger" "bg-gray-400"}} py-1 ms-2"
                >
                  {{this.pendingChangeReviewCount}}
                </span>
              </div>
            </nav.tab>
          </tabs.navigation>
        </NoRouteTabs>
      </Portal>
    {{/if}}

    <div class="flex-fill overflow-hidden" ...attributes>
      <div
        class="component-work-instruction-editor-library row position-relative bg-white h-100 g-0"
        {{didUpdate this.onDidUpdate.perform @currentWorkInstruction}}
      >
        <div class="col h-100 min-w-0">
          <div class="d-flex flex-column h-100">
            <div class="library-top-controls {{if @isModal "p-4 pb-3" "p-3 pt-1 pb-4"}}">
              <div class="row g-2">

                <div class="col {{if @isModal "col-lg-4" "col-lg-3"}}">
                  <SearchLive
                    @inputClass="library-search"
                    @searchValue={{this.searchTerm}}
                    @onInput={{this.onSearchInput.perform}}
                  />
                </div>

                {{#unless this.isApprovalView}}
                  <div class="col-auto">
                    <BsButtonGroup
                      @value={{this.currentLibraryView}}
                      @type="radio"
                      @size="sm"
                      @onChange={{this.toggleView}}
                      class="library-view-toggle"
                    as |bg|>
                      <bg.button
                        class="btn-thumb-view" @value="thumb" disabled={{eq this.currentLibraryView "approval"
                      }}>
                        <FaIcon @icon="border-all" @prefix="fas" class="fa-fw" />
                        <BsTooltip @title={{t "workInstructions.library.gridView"}} />
                      </bg.button>
                      <bg.button class="btn-list-view" @value="list" disabled={{eq this.currentLibraryView "approval"}}>
                        <FaIcon @icon="list" @prefix="fas" class="fa-fw" />
                        <BsTooltip @title={{t "workInstructions.library.listView"}} />
                      </bg.button>
                    </BsButtonGroup>
                  </div>
                {{/unless}}

                {{#unless (or @isModal this.isApprovalView)}}
                  <div class="col-auto ms-auto">
                    <FileInput
                      accept={{this.supportedFileTypes}}
                      multiple={{true}}
                      @disabled={{@importDisabled}}
                      @onChange={{this.importWorkInstructions.perform}}
                      class="library-import dropdown-item"
                    >
                      <BsButton
                        class="library-import"
                        @size="sm"
                        @disabled={{or @importDisabled this.isApprovalView}}
                      >
                        {{t "import"}}
                      </BsButton>
                    </FileInput>
                  </div>
                  <div class="col-auto">
                    <AddButton
                      class="add-folder"
                      @size="sm"
                      @type="secondary"
                      @onClick={{this.addFolder}}
                      @disabled={{or @addFolderDisabled this.searchTerm this.selectedFolder}}
                    >
                      {{t "folder"}}
                      <BsTooltip @title={{t "workInstructions.createFolder"}} />
                    </AddButton>
                  </div>
                  <div class="col-auto">
                    <AddButton
                      class="wie-create-button"
                      @onClick={{@createStart}}
                      @size="sm"
                      @disabled={{not @canCreate}}
                    >
                      {{t "create"}}
                    </AddButton>
                  </div>
                {{/unless}}
              </div>
            </div>

            {{#if this.selectedFolder}}
              <div class="folder-details d-flex align-items-center mt-2 {{if @isModal "px-4" "px-3"}}">
                <FaIcon @icon="folder" @prefix="far" class="text-gray-300 me-2" />
                <div class="selected-folder-title text-truncate fw-semibold {{if this.isListView "fs-4"}}">
                  {{this.selectedFolder.name}}
                </div>
                {{#if (and this.currentUser.isAdmin (not @isModal))}}
                  <BsButton
                    @type="link"
                    class="folder-edit-btn link-gray-900 fw-semibold p-0 ms-2"
                    @onClick={{fn this.folderSettings this.selectedFolder}}
                  >
                    <FaIcon @icon="gear" @prefix="fas" @size="sm" class="me-1"/>
                    <BsTooltip @title={{t "workInstructions.library.folderSettings"}} />
                  </BsButton>
                {{/if}}
              </div>

              {{#if (and (not this.workInstructionCount) (not this.searchTerm))}}
                <div class="empty-folder-notice {{if @isModal "px-4" "px-3"}}">
                  {{t "workInstructions.folderIsEmpty"}}
                </div>
              {{/if}}
            {{/if}}

            <div class="
              library-contents flex-fill eflex-scroll {{if this.isListView "pb-0" "p-3 py-2"}} {{if @isModal "px-0"}}
            ">
              {{#if this.showSpinner}}
                <div class="wie-spinner-overlay">
                  <Spinner />
                </div>
              {{/if}}

              {{#if this.isListView}}
                <ListView
                  @selectedCards={{this.selectedCards}}
                  @selectedFolder={{this.selectedFolder}}
                  @isAllSelected={{this.isAllSelected}}
                  @saveFolder={{this.saveFolder.perform}}
                  @currentWorkInstruction={{@currentWorkInstruction}}
                  @selectableFolders={{this.selectableFolders}}
                  @selectableLibraryItems={{this.selectableLibraryItems}}
                  @selectAll={{this.selectAll}}
                  @moveToFolder={{this.moveToFolder.perform}}
                  @cardSelected={{this.cardSelected}}
                  @cardMultiselect={{this.cardMultiselect}}
                  @loadFolder={{this.loadFolder.perform}}
                  @dropOnFolder={{this.dropOnFolder.perform}}
                  @cardDoubleClick={{this.loadWorkInstruction.perform}}
                  @delete={{this.delete.perform}}
                  @edit={{this.edit.perform}}
                  @replace={{this.replace.perform}}
                  @open={{this.loadFolder.perform}}
                  @folderSettings={{this.folderSettings}}
                  @export={{@export}}
                  @isReadOnly={{@isReadOnly}}
                  @isModal={{@isModal}}
                  @multiselectDisabled={{@multiselectDisabled}}
                  @isMultiselectMode={{this.isMultiselectMode}}
                />
              {{else if this.isApprovalView}}
                <ApprovalView
                  @isAllSelected={{this.isAllSelected}}
                  @currentWorkInstruction={{@currentWorkInstruction}}
                  @selectedCards={{this.selectedCards}}
                  @selectableLibraryItems={{this.selectableLibraryItems}}
                  @cardSelected={{this.cardSelected}}
                  @cardMultiselect={{this.cardMultiselect}}
                  @setCardSelectedTrue={{this.setCardSelectedTrue}}
                  @cardDoubleClick={{this.loadWorkInstruction.perform}}
                  @delistInstructions={{this.delistInstructions.perform}}
                  @selectAll={{this.selectAll}}
                  @isReadOnly={{@isReadOnly}}
                />
              {{else}}
                <ThumbView
                  @selectedFolder={{this.selectedFolder}}
                  @selectableFolders={{this.selectableFolders}}
                  @selectableLibraryItems={{this.selectableLibraryItems}}
                  @currentWorkInstruction={{@currentWorkInstruction}}
                  @selectedCards={{this.selectedCards}}
                  @deselectAll={{this.deselectAll}}
                  @saveFolder={{this.saveFolder.perform}}
                  @cardSelected={{this.cardSelected}}
                  @cardMultiselect={{this.cardMultiselect}}
                  @cardDoubleClick={{this.loadWorkInstruction.perform}}
                  @dropOnFolder={{this.dropOnFolder.perform}}
                  @loadFolder={{this.loadFolder.perform}}
                  @moveToFolder={{this.moveToFolder.perform}}
                  @delete={{this.delete.perform}}
                  @replace={{this.replace.perform}}
                  @edit={{this.edit.perform}}
                  @open={{this.loadFolder.perform}}
                  @folderSettings={{this.folderSettings}}
                  @export={{@export}}
                  @isReadOnly={{@isReadOnly}}
                  @isModal={{@isModal}}
                  @isMultiselectMode={{this.isMultiselectMode}}
                />
              {{/if}}

              {{#if (and this.isApprovalView this.isMultiselectMode)}}
                <div class="
                  library-multiselect
                  sticky-bottom row justify-content-center g-1 rounded mt-3 bg-gray-900 bg-opacity-75 p-3
                ">
                  <div class="col-auto">
                    <BsButton
                      @size="sm"
                      @onClick={{this.deselectAll}}
                    >
                      {{t "cancel"}}
                    </BsButton>
                  </div>
                  <div class="col-auto">
                    <PortalTarget @name="multiselect-change-review" class="row g-1" />
                  </div>
                </div>
              {{/if}}

              {{#if (and (not this.workInstructionCount) (not this.selectedFolder))}}
                <div class="empty-notice d-flex align-items-center justify-content-center h-100 px-3">
                  {{#if this.searchTerm}}
                    {{t "workInstructions.noResults"}}
                  {{else}}
                    {{t "plant.station.jem.emptyLibrary"}}
                  {{/if}}
                </div>
              {{/if}}

              {{#if this.showLoadButton}}
                <div class="d-flex justify-content-center px-3 mt-3">
                  <BsButton
                    @size="sm"
                    class="
                      load-btn
                      {{if @isReadOnly "load-btn-modal" "load-btn-normal"}}
                    "
                    @type="primary"
                    @onClick={{this.loadMore.perform}}
                  >
                    {{t "grid.loadMore"}}
                  </BsButton>
                </div>
              {{/if}}
            </div>
          </div>
        </div>
        {{#unless this.isApprovalView}}
          <div class="{{if @isModal "col-5" "col-4"}} border-start border-gray-200 h-100">
            <div class="d-flex flex-column h-100 px-3 {{if @isModal "py-3"}}">
              {{#if (eq this.selectedCards.length 1)}}
                {{#let (get this.selectedCards 0) as |selectedItem|}}
                  <div class="d-flex justify-content-end mb-2">
                    <ActionDropdown
                      @libraryItem={{selectedItem}}
                      @selectedFolder={{this.selectedFolder}}
                      @selectedCards={{this.selectedCards}}
                      @isReadOnly={{@isReadOnly}}
                      @edit={{this.edit.perform}}
                      @folderSettings={{this.folderSettings}}
                      @open={{this.loadFolder.perform}}
                      @moveToFolder={{this.moveToFolder.perform}}
                      @export={{@export}}
                      @replace={{this.replace.perform}}
                      @delete={{this.delete.perform}}
                      class="align-self-end d-flex align-items-center"
                    />
                  </div>

                  <div class="
                    h-50 flex-shrink-0 position-relative rounded overflow-hidden mb-3
                    {{if (or selectedItem.url selectedItem.displayImageUrl) "bg-gray-200"}}
                  ">
                    {{#unless selectedItem.isFolder}}
                      <div class="position-absolute top-0 end-0 z-1">
                        <BsButton
                          class="expanded-preview d-flex shadow p-1 m-1"
                          @size="sm"
                          @onClick={{fn (mut this.showExpandPreviewModal) true}}
                        >
                          <FaIcon @icon="magnifying-glass" />
                          <BsTooltip @title={{t "preview"}} />
                        </BsButton>
                      </div>
                    {{/unless}}

                    {{#if selectedItem.url}}
                      <UrlViewer @url={{selectedItem.url}} />
                    {{else if selectedItem.displayImageUrl}}
                      <img
                        src={{if @isReadOnly selectedItem.deployedImageUrl selectedItem.displayImageUrl}}
                        class="img-responsive"
                      />
                    {{else}}
                      <span class="img-responsive">
                        <FaIcon @icon="folder" class="text-gray-300 opacity-50 w-100 h-100" />
                      </span>
                    {{/if}}
                  </div>

                  <div class="flex-fill eflex-scroll">
                    <div class="mb-3">
                      <div class="d-flex align-items-center">
                        <h6 class="text-truncate mb-0">
                          {{or selectedItem.deployedName selectedItem.name}}
                          <BsTooltip @title={{or selectedItem.deployedName selectedItem.name}} />
                        </h6>

                        {{#if (or
                          selectedItem.isDynamic
                          (and this.workInstructionRepo.isApprovalActive selectedItem.isInstruction (not @isModal))
                        )}}
                          <div class="lh-1 d-flex ms-auto">
                            {{#if selectedItem.isDynamic}}
                              <span class="badge bg-primary user-select-none fs-5 ms-1">
                                <FaIcon @icon="bolt" @size="sm" class="me-1" />
                                {{t "dynamic"}}
                                <BsTooltip @title={{t "jem.dynamicWorkInstruction"}} />
                              </span>
                            {{/if}}

                            {{#if selectedItem.isPending}}
                              <span class="badge bg-warning text-gray-900 user-select-none fs-5 ms-1">
                                {{t "wie.approvals.pending"}}
                              </span>
                            {{else if selectedItem.isApproved}}
                              <span class="badge bg-success fs-5 text-gray-900 user-select-none ms-1">
                                {{t "wie.approvals.approved"}}
                              </span>
                            {{else if selectedItem.isRejected}}
                              <span class="badge bg-danger user-select-none fs-5 ms-1">
                                {{t "wie.approvals.rejected"}}
                              </span>
                            {{else if selectedItem.deployedImageUrl}}
                              <span class="badge bg-gray-200 user-select-none fs-5 text-gray-900 ms-1">
                                {{t "wie.approvals.deployed"}}
                              </span>
                            {{/if}}

                            {{#if selectedItem.approvalIconTooltip}}
                              <BsTooltip @title={{selectedItem.approvalIconTooltip}} />
                            {{/if}}
                          </div>
                        {{/if}}
                      </div>

                      <span class="text-gray-600 text-truncate">
                        {{#if selectedItem.isInstruction}}
                          {{t "plant.station.workInstructionEditor"}}
                        {{else if selectedItem.isFile}}
                          {{t (get this.mimeTypeDescriptions selectedItem.mime)}}
                        {{else}}
                          {{t "folder"}}
                        {{/if}}
                      </span>
                    </div>

                    {{#if (and (eq selectedItem.mime 'application/pdf') @onPdfPageInput)}}
                      <div class="mb-3">
                        <label class="form-label">
                          {{t "page"}}
                        </label>
                        <NumberControl
                          class="pdf-page w-25"
                          @size="sm"
                          @onChange={{@onPdfPageInput}}
                        />
                      </div>
                    {{/if}}

                    {{#if (or selectedItem.isInstruction selectedItem.isFolder)}}
                      <p class="fw-bold mb-2">{{t "information"}}</p>

                      {{#if selectedItem.isInstruction}}
                        <div class="d-flex justify-content-between fs-4">
                          <span class="text-gray-600">{{t "author"}}</span>
                          <span>{{selectedItem.authorUsername}}</span>
                        </div>
                      {{/if}}

                      {{#if (and selectedItem.width selectedItem.height)}}
                        <div class="d-flex justify-content-between fs-4">
                          <span class="text-gray-600">{{t "wieList.dimensions"}}</span>
                          <span>{{selectedItem.width}} x {{selectedItem.height}}</span>
                        </div>
                      {{/if}}

                      {{#if selectedItem.isFolder}}
                        <div class="d-flex justify-content-between fs-4">
                          <span class="text-gray-600">{{t "items"}}</span>
                          <span>{{selectedItem.workInstructions.length}}</span>
                        </div>
                      {{/if}}

                      {{#if selectedItem.updatedAtFormatted}}
                        <div class="d-flex justify-content-between fs-4">
                          <span class="text-gray-600">{{t "modified"}}</span>
                          <span>{{selectedItem.updatedAtFormatted}}</span>
                        </div>
                      {{/if}}
                    {{/if}}
                  </div>

                  {{#unless @isModal}}
                    <div class="d-flex justify-content-end">
                      <div class="d-flex align-items-center mt-4">
                        {{#if selectedItem.isInstruction}}
                          <BsButton
                            @type="primary"
                            @onClick={{fn this.loadWorkInstruction.perform selectedItem}}
                            class="text-capitalize"
                          >
                            {{t "workInstruction.openInEditor"}}
                          </BsButton>
                        {{else if selectedItem.isFolder}}
                          <BsButton
                            @type="primary"
                            @onClick={{fn this.loadFolder.perform selectedItem}}
                            class="text-capitalize"
                          >
                            {{t "workInstruction.openFolder"}}
                          </BsButton>
                        {{/if}}
                      </div>
                    </div>
                  {{/unless}}
                {{/let}}
              {{else if this.isMultiselectMode}}
                <div class="library-multiselect row align-items-center justify-content-center gx-2 w-100 h-100">
                  <div class="col-auto">
                    <h6 class="me-2 mb-0">{{t "wieLibrary.itemsSelected" count=this.selectedCards.length}}</h6>
                  </div>
                  <div class="col-auto">
                    <ActionDropdown
                      class="library-multiselect-controls"
                      @multiselectOnly={{true}}
                      @isReadOnly={{@isReadOnly}}
                      @selectedFolder={{this.selectedFolder}}
                      @export={{@export}}
                      @moveToFolder={{this.moveToFolder.perform}}
                      @delete={{this.delete.perform}}
                      @selectedCards={{this.selectedCards}}
                      @deselectAll={{this.deselectAll}}
                    />
                  </div>
                  <div class="col-auto">
                    <BsButton
                      class="library-multiselect-cancel"
                      @onClick={{this.deselectAll}}
                      @size="sm"
                    >
                      {{t "cancel"}}
                    </BsButton>
                  </div>
                </div>
              {{else}}
                <div class="d-flex align-items-center justify-content-center h-100">
                  <h6 class="text-gray-500 fw-semibold mb-0">
                    {{t "library.select"}}
                  </h6>
                </div>
              {{/if}}
            </div>
          </div>
        {{/unless}}
      </div>
    </div>

    {{#if this.showConfirmModal}}
      <ConfirmModal
        @onCancel={{fn (mut this.showConfirmModal) false}}
        @onConfirm={{this.selectedConfirmAction.perform}}
      >
        {{#each this.confirmModalText as |text|}}
          <div class="fw-semibold mb-2">
            <FaIcon @icon="circle-exclamation" @prefix="fas" class="text-danger me-1"/>
            {{text}}
          </div>
        {{/each}}
      </ConfirmModal>
    {{/if}}

    {{#if this.showFolderSettingsModal}}
      <StandardModal
        class="edit-folder-modal jem"
        @size="sm"
        @onCancel={{this.cancelFolderSettings}}
        @onConfirm={{this.confirmFolderSettings.perform}}
      as |modal|>
        <modal.header @text={{t "folder"}} />
        <modal.body>
          <BsForm @model={{this.targetCard}} as |form| >
            <form.element
              @controlType="text"
              @property="name"
              @label={{t "name"}}
              class={{unless this.targetCard.isNew "mb-5"}}
            as |el|>
              <el.control
                class="edit-folder-title"
                placeholder={{t "workInstructions.untitledFolder"}}
                disabled={{not this.workInstructionRepo.userCanEdit this.targetCard}}
                {{onKey 'Enter' this.confirmFolderSettings.perform}}
                {{autofocus}}
              />
            </form.element>
            {{#if (and (not this.targetCard.isNew) this.workInstructionRepo.isApprovalActive)}}
              <form.element class="wie-editor-select" @label={{t "settings.users.wieEditorTags"}}>
                <TagsSelect
                  @siblings={{this.workInstructionRepo.folders}}
                  @taggedModel={{this.targetCard}}
                  @fieldName="wieEditorTags"
                  @additionalOptions={{this.workInstructionRepo.systemUserWieEditorTags}}
                />
              </form.element>

              <form.element class="wie-approver-select" @label={{t "settings.users.wieApproverTags"}}>
                <TagsSelect
                  @siblings={{this.workInstructionRepo.folders}}
                  @taggedModel={{this.targetCard}}
                  @fieldName="wieApproverTags"
                  @additionalOptions={{this.workInstructionRepo.systemUserWieApproverTags}}
                />
              </form.element>

              <form.element class="wie-deployer-select" @label={{t "settings.users.wieDeployerTags"}}>
                <TagsSelect
                  @siblings={{this.workInstructionRepo.folders}}
                  @taggedModel={{this.targetCard}}
                  @fieldName="wieDeployerTags"
                  @additionalOptions={{this.workInstructionRepo.systemUserWieDeployerTags}}
                />
              </form.element>
            {{/if}}
          </BsForm>
        </modal.body>
        <modal.footer as |footer|>
          <footer.confirm>{{if this.targetCard.isNew (t "create") (t "save")}}</footer.confirm>
          <footer.close />
        </modal.footer>
      </StandardModal>
    {{/if}}

    {{#if this.showImportModal}}
      <StandardModal
        class="import-modal"
        @onCancel={{this.cancelImport}}
      as |modal|>
        <modal.header @text={{t "import"}} />
        <modal.body>
          <div class="fw-semibold mb-4">
            <FaIcon @icon="circle-info" @prefix="fas" class="text-primary me-1"/>
            {{this.confirmImportText}}
          </div>
          <div>{{t "modals.callToAction"}}</div>
        </modal.body>
        <modal.footer as |footer|>
          <BsButton
            class="confirm-import-as-file text-white"
            @type="primary"
            @onClick={{this.confirmImport.perform}}
          >
            {{t "workInstructions.importAsFile"}}
          </BsButton>
          <BsButton
            class="confirm-import-as-instruction"
            @type="outline-primary"
            @onClick={{this.confirmImport.perform true}}
          >
            {{t "workInstructions.importAsInstruction"}}
          </BsButton>
          <footer.close />
        </modal.footer>
      </StandardModal>
    {{/if}}

    {{#if (and (eq this.selectedCards.length 1) this.showExpandPreviewModal)}}
      {{#let (get this.selectedCards 0) as |selectedItem|}}
        {{#unless selectedItem.isFolder}}
          <StandardModal
            class="expanded-preview-modal"
            @contentClass="h-100"
            @onCancel={{fn (mut this.showExpandPreviewModal) false}}
            @size="xl"
          as |modal|>
            <modal.header class="pb-0" as |header|>
              <div class="d-flex align-items-center w-100">
                <div class="flex-fill d-flex align-items-center min-w-0">
                  <h5 class="modal-title lh-sm text-truncate">
                    {{or selectedItem.deployedName selectedItem.name}}
                  </h5>
                </div>
                <header.close />
              </div>
            </modal.header>
            <modal.body>
              <div class="d-flex align-items-center justify-content-center bg-gray-100 h-100 position-relative">
                {{#if selectedItem.url}}
                  <UrlViewer @url={{selectedItem.url}} />
                {{else}}
                  <img
                    src={{if @isReadOnly selectedItem.deployedImageUrl selectedItem.displayImageUrl}}
                    class="img-responsive"
                  />
                {{/if}}
              </div>
            </modal.body>
          </StandardModal>
        {{/unless}}
      {{/let}}
    {{/if}}
  `, {
            component: this,
            eval () {
                return eval(arguments[0]);
            }
        });
    }
}
